// Utility to group timezones by their UTC offset to reduce redundancy
// This helps show "canonical" timezone names instead of listing all cities with the same offset

export interface GroupedTimezone {
  canonical: string; // Main timezone name to display
  offset: string; // UTC offset like "+01:00"
  alternatives: string[]; // Other timezones with same offset
}

// Get UTC offset for a timezone at current date
function getTimezoneOffset(tz: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "longOffset",
    });
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find((p) => p.type === "timeZoneName");
    if (offsetPart?.value && offsetPart.value.startsWith("GMT")) {
      const offset = offsetPart.value.replace("GMT", "");
      return offset === "" ? "+00:00" : offset;
    }
    return "+00:00";
  } catch {
    return "+00:00";
  }
}

// Canonical timezones to prefer (major cities/regions)
const PREFERRED_CANONICAL = [
  "UTC",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Rome",
  "Europe/Madrid",
  "Europe/Amsterdam",
  "Europe/Brussels",
  "Europe/Vienna",
  "Europe/Warsaw",
  "Europe/Prague",
  "Europe/Athens",
  "Europe/Helsinki",
  "Europe/Stockholm",
  "Europe/Copenhagen",
  "Europe/Oslo",
  "Europe/Lisbon",
  "Europe/Dublin",
  "Europe/Zurich",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Vancouver",
  "America/Mexico_City",
  "America/Sao_Paulo",
  "America/Buenos_Aires",
  "America/Santiago",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Hong_Kong",
  "Asia/Singapore",
  "Asia/Seoul",
  "Asia/Bangkok",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Jakarta",
  "Asia/Manila",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Australia/Brisbane",
  "Australia/Perth",
  "Pacific/Auckland",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Africa/Lagos",
  "Africa/Nairobi",
];

export function groupTimezones(timezones: string[]): GroupedTimezone[] {
  // Group by offset
  const offsetGroups = new Map<string, string[]>();

  timezones.forEach((tz) => {
    const offset = getTimezoneOffset(tz);
    if (!offsetGroups.has(offset)) {
      offsetGroups.set(offset, []);
    }
    offsetGroups.get(offset)!.push(tz);
  });

  // Convert to GroupedTimezone array
  const grouped: GroupedTimezone[] = [];

  offsetGroups.forEach((zones, offset) => {
    // Find canonical timezone (prefer major cities)
    let canonical = zones[0];
    for (const preferred of PREFERRED_CANONICAL) {
      if (zones.includes(preferred)) {
        canonical = preferred;
        break;
      }
    }

    const alternatives = zones.filter((z) => z !== canonical);

    grouped.push({
      canonical,
      offset,
      alternatives,
    });
  });

  // Sort by offset
  grouped.sort((a, b) => {
    const offsetA = parseOffset(a.offset);
    const offsetB = parseOffset(b.offset);
    return offsetA - offsetB;
  });

  return grouped;
}

function parseOffset(offset: string): number {
  // Parse "+05:30" or "-08:00" to minutes
  const match = offset.match(/([+-])(\d{2}):(\d{2})/);
  if (!match) return 0;
  const [, sign, hours, minutes] = match;
  const totalMinutes = parseInt(hours) * 60 + parseInt(minutes);
  return sign === "-" ? -totalMinutes : totalMinutes;
}

export function formatTimezoneDisplay(tz: string, offset: string): string {
  return `${tz} (UTC${offset})`;
}

/**
 * Get the current time in a specific timezone as hour and minute
 */
export function getTimeInTimezone(
  date: Date,
  timezone: string,
): { hour: number; minute: number; day: number } {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    weekday: "short",
  });

  const parts = formatter.formatToParts(date);
  const hour = parseInt(parts.find((p) => p.type === "hour")?.value || "0", 10);
  const minute = parseInt(
    parts.find((p) => p.type === "minute")?.value || "0",
    10,
  );
  const weekday = parts.find((p) => p.type === "weekday")?.value || "Sun";

  // Map weekday to day number
  const dayMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  const day = dayMap[weekday] || 0;

  return { hour, minute, day };
}

/**
 * Check if a local time slot matches schedule hours in schedule's timezone
 */
export function isTimeInSchedule(
  localDate: Date,
  localHour: number,
  localMinute: number,
  scheduleTimezone: string,
  scheduleRules: {
    days: string[];
    startHour: number;
    startMin: number;
    durationMin: number;
  }[],
): boolean {
  // Create a date representing the local time slot
  const slotDate = new Date(localDate);
  slotDate.setHours(localHour, localMinute, 0, 0);

  // Get what time this is in the schedule's timezone
  const timeInScheduleTz = getTimeInTimezone(slotDate, scheduleTimezone);

  // Check if this time matches any schedule rule
  for (const rule of scheduleRules) {
    // Map day number to RRULE format
    const dayMap = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
    const currentDay = dayMap[timeInScheduleTz.day];

    // Check if day matches
    if (!rule.days.includes(currentDay)) continue;

    // Check if time is within the slot
    const slotMinutes = timeInScheduleTz.hour * 60 + timeInScheduleTz.minute;
    const startMinutes = rule.startHour * 60 + rule.startMin;
    const endMinutes = startMinutes + rule.durationMin;

    if (slotMinutes >= startMinutes && slotMinutes < endMinutes) {
      return true;
    }
  }

  return false;
}
